import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useApi } from '../hooks';
import { CustomerAutocomplete } from './CustomerAutocomplete';
import { customerAttachmentErrorText } from './attachedCustomer';

/* The Change control's modal (UND-987): search-and-select an existing customer, or explicitly choose
   "Make a new customer" to detach and let the server create one fresh from the application's own
   insured and address — PUT .../customer's null-customerId branch. Exactly one of those two is what
   Confirm sends; there is no third "leave it" option because this dialog is never opened except to
   replace the current attachment. */
const ChangeCustomerDialog = ({ open, application, onClose, onChanged }) => {
  const { changeApplicationCustomer } = useApi();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [makingNew, setMakingNew] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Each opening starts clean: a selection (or an error) left over from a previous open, or a previous
  // failed attempt, must never carry silently into the next one.
  useEffect(() => {
    if (open) {
      setSelectedCustomer(null);
      setMakingNew(false);
      setError(null);
    }
  }, [open]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setMakingNew(false);
    }
  };

  const handleMakeNew = () => {
    setSelectedCustomer(null);
    setMakingNew(true);
  };

  const canConfirm = !!selectedCustomer || makingNew;

  const handleConfirm = async () => {
    if (!canConfirm || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await changeApplicationCustomer(application.id, selectedCustomer ? selectedCustomer.customerId : null);
      setSubmitting(false);
      onClose();
      if (onChanged) {
        await onChanged();
      }
    } catch (err) {
      setSubmitting(false);
      setError(customerAttachmentErrorText(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change customer</DialogTitle>
          <DialogDescription>Search for an existing customer, or make a new one from this application's own details.</DialogDescription>
        </DialogHeader>
        <CustomerAutocomplete value={selectedCustomer} onChangeValue={handleSelectCustomer} label="Existing customer" disabled={submitting} />
        <Button variant={makingNew ? 'default' : 'outline'} onClick={handleMakeNew} disabled={submitting}>
          Make a new customer
        </Button>
        {makingNew ? (
          <p className="text-sm text-muted-foreground">A new customer will be created from this application's insured and address.</p>
        ) : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ChangeCustomerDialog };
